import React, { useContext, useEffect, useState } from "react";
import { CommandBarButton, DefaultButton, DetailsList, Dialog, IColumn, IconButton, IGroup, Panel, Pivot, PivotItem, PrimaryButton, SearchBox, SelectionMode, Stack, Text, TextField } from "@fluentui/react";
import { UnauthorizedAccessError } from "../errors";
import { AdminContext } from "../stores";
import Loading from "../components/loading/Loading";
import Error from "../components/error/Error";
import styles from "./AdminPage.module.scss";
import { Mapping } from "../models";
import LogoutLinkGeneration from "../components/logoutLinkGeneration/LogoutLinkGeneration";

export const AdminPage: React.FC = () => {

    const adminContext = useContext(AdminContext);
    const [error, setError] = useState<any>();

    const [panelOpened, setPanelOpened] = useState(false);
    const [dialogOpened, setDialogOpened] = useState(false);
    const [search, setSearch] = useState("");

    const [reportId, setReportId] = useState("");
    const [reportIdError, setReportIdError] = useState<string>();
    const [reportRelativeUrl, setReportRelativeUrl] = useState("");
    const [reportRelativeUrlError, setReportRelativeUrlError] = useState<string>();
  
    const [selectedMapping, setSelectedMapping] = useState<Mapping>(undefined);
    const [creating, setCreating] = useState(false);
    const [deleting, setDeleting] = useState(false);

    useEffect(() => {
        const initialize = async () => {
            try {
                await adminContext.getMappings();
            } catch (error) {
                if (error instanceof UnauthorizedAccessError) {
                    setError("Nie posiadasz wystarczających uprawnień do panelu administratora.");
                } else {
                    setError("Wystąpił problem podczas wczytywania panelu administratora.");
                }
            }
        };

        document.title = "CUK Raporty | Administracja";
        initialize();
    }, []);

    if (error) {
        return <Error content={error} />;
    }

    if (adminContext.mappings === undefined) {
        return <Loading label="Wczytywanie panelu administracyjnego..."/>;
    }
    
    const onAddNewMapping = async () => {
        let valid = true;
        const loweredRelativeUrl = reportRelativeUrl.toLowerCase();
        if (adminContext.mappings.map(mapping => mapping.relativeUrl.toLowerCase()).includes(loweredRelativeUrl)) {
            setReportRelativeUrlError("Istnieje już mapowanie dla tego URL");
            valid = false;
        }

        if (/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(reportId) === false) {
            setReportIdError("Wymagana poprawna wartość GUID");
            valid = false;
        }

        if (valid) {
            setCreating(true);

            await adminContext.createMapping({
                reportId: reportId,
                relativeUrl: reportRelativeUrl
            });
            await adminContext.getMappings();   

            setPanelOpened(false);
            setCreating(false);
            setReportId("");
            setReportRelativeUrl("");
        }
    };

    const onDeleteMapping = async () => {
        setDeleting(true);
        await adminContext.deleteMapping(selectedMapping.relativeUrl);
        await adminContext.getMappings();
        setDialogOpened(false);
        setDeleting(false);
    };

    const columns: IColumn[] = [
        { key: "relativeUrl", name: "URL", fieldName: "relativeUrl", minWidth: 300, isResizable: true },
        { key: "reportId", name: "ID raportu", fieldName: "reportId", minWidth: 250, isResizable: true },
        { key: "delete", name: "", fieldName: "", minWidth: 30, onRender: (item) => <IconButton iconProps={{iconName: "Delete", styles: {root: {color: "red"}}}} onClick={() => { setSelectedMapping(item as Mapping); setDialogOpened(true); }} /> }
    ];

    const sortedItems = adminContext.mappings.filter(mapping => search === "" || mapping.relativeUrl.includes(search) || mapping.reportId.includes(search)).sort((a, b) => a.reportId.localeCompare(b.reportId));
    const reportIds = sortedItems.map(item => item.reportId);
    const uniqueReportIds = sortedItems.map(mapping => mapping.reportId).filter((value, index, self) => self.indexOf(value) === index);
    const groups = uniqueReportIds.map((reportId, index, all): IGroup => ({
        key: reportId,
        name: `raport ${reportId}`,
        startIndex: reportIds.indexOf(reportId),
        count: reportIds.lastIndexOf(reportId) - reportIds.indexOf(reportId) + 1
    }));

    return (
        <>
            <Stack horizontalAlign="center" className={styles.page} tokens={{childrenGap: 20}}>
                <Pivot styles={{text: {fontSize: 20}, icon: {fontSize: 20}}}>
                    <PivotItem headerText="Mapowanie raportów" itemIcon="Link" itemCount={adminContext.mappings.length}>
                        <div style={{marginTop: 20}}>
                            <Stack horizontal horizontalAlign="stretch" styles={{root: {height: 38}}} tokens={{childrenGap: 10}}>
                                <PrimaryButton text="Dodaj" iconProps={{iconName: "Add"}} onClick={() => setPanelOpened(true)} />
                                {/* <CommandBarButton text="Usuń" iconProps={{iconName: "Delete"}} onClick={() => setDialogOpened(true)} disabled={selectedMapping === undefined} /> */}
                                <Stack.Item grow={1}>
                                    <SearchBox placeholder="Szukaj" value={search} onChange={(_, value) => setSearch(value ?? "")} underlined />
                                </Stack.Item>
                            </Stack> 
                            <DetailsList
                                items={sortedItems}
                                columns={columns}
                                groups={groups}
                                selectionMode={SelectionMode.none}
                            />
                        </div>
                    </PivotItem>
                    <PivotItem headerText="Generator linków do wylogowania" itemIcon="SignOut">
                        <div style={{marginTop: 20}}>
                            <LogoutLinkGeneration mappings={adminContext.mappings} />
                        </div>
                    </PivotItem>
                </Pivot>
            </Stack>
            <Panel 
                headerText="Dodaj nowe mapowanie" 
                onRenderFooterContent={() => <Stack horizontal tokens={{childrenGap: 10}}>
                    <PrimaryButton text="Dodaj" onClick={onAddNewMapping} disabled={reportId === "" || reportRelativeUrl === "" || creating} />
                    <DefaultButton text="Anuluj" onClick={() => setPanelOpened(false)} disabled={creating} />
                </Stack>} 
                isFooterAtBottom={true} 
                isOpen={panelOpened} 
                onDismiss={() => setPanelOpened(false)}
            >
                <Stack tokens={{childrenGap: 10}}>
                    <TextField label="URL" prefix={window.location.origin} placeholder="/cuk/zarzad" value={reportRelativeUrl} onChange={(_, value) => { setReportRelativeUrl(value); setReportRelativeUrlError(undefined); }} errorMessage={reportRelativeUrlError} disabled={creating} />
                    <TextField label="ID raportu" placeholder="75713a57-9ba0-4a8d-8987-84fa37213465" value={reportId } onChange={(_, value) => { setReportId(value); setReportIdError(undefined); }} errorMessage={reportIdError} disabled={creating} />    
                </Stack>
            </Panel>
            <Dialog hidden={!dialogOpened} dialogContentProps={{title: "Usuwanie mapowania"}}>
                <Stack tokens={{childrenGap: 10}}>
                    <Text>Czy na pewno chcesz usunąć mapowanie?</Text>
                    <Stack horizontalAlign="center">
                        <Text><strong>{selectedMapping?.relativeUrl}</strong></Text>
                    </Stack>
                    <Stack horizontal tokens={{childrenGap: 10}} horizontalAlign="end" style={{marginTop: 30}}>
                        <PrimaryButton text="Usuń" onClick={onDeleteMapping} disabled={deleting} />
                        <DefaultButton text="Anuluj" onClick={() => setDialogOpened(false)} disabled={deleting} />
                    </Stack>
                </Stack>
            </Dialog>
        </>
    );
};
